// Polygon.io WebSocket Service for Real-time Data
// Handles both stock aggregates and option trades.
// When FMP_API_KEY is set, chart stock real-time uses FMP WebSocket instead of Polygon for stocks only.
import {
    isFmpEnabled,
    getFmpApiKey,
    FmpStockWebSocketService,
} from "./fmpStockService";

/** Polygon stock trade (T) — used to drive tick-level chart price. */
export interface StockTrade {
    ev: "T";
    sym: string;
    p: number;
    s: number;
    t: number;
    x?: number;
}

export interface StockAggregate {
    ev: "A" | "AM"; // Second or minute aggregate
    sym: string; // Ticker symbol
    /** Last trade price when event was synthesized from T */
    p?: number;
    v: number; // Tick volume
    av: number; // Today's accumulated volume
    op: number; // Today's official opening price
    vw: number; // Volume weighted average price
    o: number; // Opening tick price
    c: number; // Closing tick price
    h: number; // Highest tick price
    l: number; // Lowest tick price
    a: number; // Today's volume weighted average price
    z: number; // Average trade size
    s: number; // Start timestamp (Unix MS)
    e: number; // End timestamp (Unix MS)
    otc?: boolean; // OTC ticker flag
}

export interface OptionTrade {
    ev: "T"; // Event type
    sym: string; // Option contract symbol
    x: number; // Exchange ID
    p: number; // Price
    s: number; // Trade size
    c: number[]; // Trade conditions
    t: number; // Timestamp (Unix MS)
    q: number; // Sequence number
    i?: string; // Trade ID
    z?: number; // Tape
    trfi?: number; // TRF ID
    trft?: number; // TRF Timestamp
}

export interface WebSocketConfig {
    apiKey: string;
    /** e.g. wss://socket.polygon.io/stocks (real-time) or wss://delayed.polygon.io/stocks */
    stockWsUrl?: string;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
}

const DEFAULT_DELAYED_STOCK_WS = "wss://delayed.polygon.io/stocks";
const REALTIME_STOCK_WS = "wss://socket.polygon.io/stocks";

function stockWsUrlFromEnv(fallback: string): string {
    const env = typeof process !== "undefined" ? process.env : ({} as NodeJS.ProcessEnv);
    const fromEnv = (
        env.REACT_APP_POLYGON_STOCK_WS_URL ||
        env.POLYGON_STOCK_WS_URL ||
        ""
    ).trim();
    return fromEnv || fallback;
}

import {
    ChartStockLiveCallback,
    ChartStockLiveTick,
} from "./chartStockLiveTypes";

export type { ChartStockLiveTick };
export type StockAggregateCallback = ChartStockLiveCallback;
export type OptionTradeCallback = (data: OptionTrade) => void;
export type ConnectionCallback = (connected: boolean) => void;

class PolygonWebSocketService {
    private stockWs: WebSocket | null = null;
    private optionWs: WebSocket | null = null;
    private config: WebSocketConfig;
    private stockCallbacks: Map<string, StockAggregateCallback[]> = new Map();
    private optionCallbacks: Map<string, OptionTradeCallback[]> = new Map();
    private connectionCallbacks: ConnectionCallback[] = [];
    private reconnectAttempts = 0;
    private isConnecting = false;
    /** Polygon requires auth_success before subscribe; aggregates may arrive as JSON arrays. */
    private stockAuthenticated = false;
    private readonly stockWsUrl: string;

    constructor(config: WebSocketConfig) {
        this.config = {
            reconnectInterval: 5000,
            maxReconnectAttempts: 10,
            ...config,
        };
        this.stockWsUrl =
            config.stockWsUrl ||
            stockWsUrlFromEnv(DEFAULT_DELAYED_STOCK_WS);

        console.log(
            "🔌 [WebSocket] Polygon WebSocket Service initialized with API key:",
            this.config.apiKey.substring(0, 10) + "...",
            "stock URL:",
            this.stockWsUrl,
        );
    }

    // Stock Aggregates WebSocket
    async connectStockAggregates(): Promise<void> {
        if (this.stockWs?.readyState === WebSocket.OPEN) {
            console.log("🔌 [WebSocket] Stock aggregates already connected");
            return;
        }

        if (this.isConnecting) {
            console.log(
                "🔌 [WebSocket] Stock aggregates connection in progress",
            );
            return;
        }

        this.isConnecting = true;

        try {
            console.log(
                "🔌 [WebSocket] Connecting to stock aggregates:",
                this.stockWsUrl,
            );

            this.stockWs = new WebSocket(this.stockWsUrl);

            this.stockWs.onopen = () => {
                console.log("✅ [WebSocket] Stock socket open — authenticating…");
                this.reconnectAttempts = 0;
                this.isConnecting = false;
                this.stockAuthenticated = false;
                this.authenticateStock();
            };

            this.stockWs.onmessage = (event) => {
                const raw =
                    typeof event.data === "string"
                        ? event.data
                        : JSON.stringify(event.data);
                setTimeout(() => {
                    try {
                        const data = JSON.parse(raw);
                        this.dispatchStockMessages(data);
                    } catch (error) {
                        console.error(
                            "❌ [WebSocket] Error parsing stock message:",
                            error,
                        );
                    }
                }, 0);
            };

            this.stockWs.onclose = (event) => {
                console.log(
                    "🔌 [WebSocket] Stock aggregates disconnected:",
                    event.code,
                    event.reason,
                );
                this.isConnecting = false;
                this.stockAuthenticated = false;
                this.notifyConnectionCallbacks(false);
                this.handleReconnect("stock");
            };

            this.stockWs.onerror = (error) => {
                console.error("❌ [WebSocket] Stock aggregates error:", error);
                console.error(
                    "❌ [WebSocket] WebSocket state:",
                    this.stockWs?.readyState,
                );
                console.error(
                    "❌ [WebSocket] API Key used:",
                    this.config.apiKey.substring(0, 10) + "...",
                );
                this.isConnecting = false;
            };
        } catch (error) {
            console.error(
                "❌ [WebSocket] Failed to connect stock aggregates:",
                error,
            );
            this.isConnecting = false;
        }
    }

    // Option Trades WebSocket
    async connectOptionTrades(): Promise<void> {
        if (this.optionWs?.readyState === WebSocket.OPEN) {
            console.log("🔌 [WebSocket] Option trades already connected");
            return;
        }

        if (this.isConnecting) {
            console.log("🔌 [WebSocket] Option trades connection in progress");
            return;
        }

        this.isConnecting = true;

        try {
            const wsUrl = "wss://socket.polygon.io/options";
            console.log("🔌 [WebSocket] Connecting to option trades:", wsUrl);

            this.optionWs = new WebSocket(wsUrl);

            this.optionWs.onopen = () => {
                console.log("✅ [WebSocket] Option trades connected");
                this.reconnectAttempts = 0;
                this.isConnecting = false;
                this.authenticateOption();
                this.notifyConnectionCallbacks(true);
            };

            this.optionWs.onmessage = (event) => {
                const raw =
                    typeof event.data === "string"
                        ? event.data
                        : JSON.stringify(event.data);
                setTimeout(() => {
                    try {
                        const data = JSON.parse(raw);
                        this.handleOptionMessage(data);
                    } catch (error) {
                        console.error(
                            "❌ [WebSocket] Error parsing option message:",
                            error,
                        );
                    }
                }, 0);
            };

            this.optionWs.onclose = (event) => {
                console.log(
                    "🔌 [WebSocket] Option trades disconnected:",
                    event.code,
                    event.reason,
                );
                this.isConnecting = false;
                this.notifyConnectionCallbacks(false);
                this.handleReconnect("option");
            };

            this.optionWs.onerror = (error) => {
                console.error("❌ [WebSocket] Option trades error:", error);
                this.isConnecting = false;
            };
        } catch (error) {
            console.error(
                "❌ [WebSocket] Failed to connect option trades:",
                error,
            );
            this.isConnecting = false;
        }
    }

    private authenticateStock(): void {
        if (this.stockWs?.readyState === WebSocket.OPEN) {
            const authMessage = {
                action: "auth",
                params: this.config.apiKey,
            };
            this.stockWs.send(JSON.stringify(authMessage));
            console.log("🔐 [WebSocket] Stock auth sent");
        }
    }

    private flushStockSubscriptions(): void {
        Array.from(this.stockCallbacks.keys()).forEach((symbol) => {
            this.sendStockSubscription(symbol);
        });
    }

    private dispatchStockMessages(raw: unknown): void {
        const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
        for (const item of list) {
            if (!item || typeof item !== "object") continue;
            const msg = item as Record<string, unknown>;
            if (msg.ev === "status") {
                if (msg.status === "auth_success") {
                    this.stockAuthenticated = true;
                    console.log("✅ [WebSocket] Stock aggregates authenticated");
                    this.flushStockSubscriptions();
                    this.notifyConnectionCallbacks(true);
                } else if (msg.status === "auth_failed") {
                    this.stockAuthenticated = false;
                    console.error(
                        "❌ [WebSocket] Stock auth failed:",
                        msg.message ?? msg.status,
                    );
                    this.notifyConnectionCallbacks(false);
                }
                continue;
            }
            if ((msg.ev === "A" || msg.ev === "AM") && msg.sym) {
                this.handleStockAggregate(msg as unknown as StockAggregate);
            } else if (msg.ev === "T" && msg.sym) {
                this.handleStockTrade(msg as unknown as StockTrade);
            }
        }
    }

    private normalizeTradeTsMs(t: number): number {
        if (!Number.isFinite(t) || t <= 0) return Date.now();
        if (t > 1e15) return Math.floor(t / 1_000_000);
        if (t > 1e12) return Math.floor(t);
        return Math.floor(t * 1000);
    }

    private handleStockTrade(data: StockTrade): void {
        const sym = String(data.sym).toUpperCase();
        const price = Number(data.p);
        if (!price || price <= 0) return;
        const agg: StockAggregate = {
            ev: "A",
            sym,
            v: Number(data.s) || 0,
            av: 0,
            op: price,
            vw: price,
            o: price,
            c: price,
            h: price,
            l: price,
            a: price,
            z: 0,
            s: this.normalizeTradeTsMs(data.t),
            e: this.normalizeTradeTsMs(data.t),
            p: price,
        };
        this.handleStockAggregate(agg);
    }

    private handleStockAggregate(data: StockAggregate): void {
        const sym = String(data.sym).toUpperCase();
        const callbacks = this.stockCallbacks.get(sym);
        if (callbacks) {
            callbacks.forEach((callback) => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(
                        "❌ [WebSocket] Error in stock callback:",
                        error,
                    );
                }
            });
        }
    }

    private authenticateOption(): void {
        if (this.optionWs?.readyState === WebSocket.OPEN) {
            const authMessage = {
                action: "auth",
                params: this.config.apiKey,
            };
            this.optionWs.send(JSON.stringify(authMessage));
            console.log("🔐 [WebSocket] Option trades authenticated");
        }
    }

    // Subscribe to stock aggregates
    subscribeToStock(symbol: string, callback: StockAggregateCallback): void {
        const sym = symbol.trim().toUpperCase();
        if (!this.stockCallbacks.has(sym)) {
            this.stockCallbacks.set(sym, []);
        }
        this.stockCallbacks.get(sym)!.push(callback);

        if (
            this.stockWs?.readyState === WebSocket.OPEN &&
            this.stockAuthenticated
        ) {
            this.sendStockSubscription(sym);
        } else if (this.stockWs?.readyState !== WebSocket.OPEN) {
            this.connectStockAggregates();
        }

        console.log(`📊 [WebSocket] Subscribed to stock aggregates: ${sym}`);
    }

    // Subscribe to option trades
    subscribeToOption(contract: string, callback: OptionTradeCallback): void {
        if (!this.optionCallbacks.has(contract)) {
            this.optionCallbacks.set(contract, []);
        }
        this.optionCallbacks.get(contract)!.push(callback);

        // Send subscription if connected
        if (this.optionWs?.readyState === WebSocket.OPEN) {
            this.sendOptionSubscription(contract);
        } else {
            // Connect if not already connected
            this.connectOptionTrades().then(() => {
                setTimeout(() => this.sendOptionSubscription(contract), 1000);
            });
        }

        console.log(`📊 [WebSocket] Subscribed to option trades: ${contract}`);
    }

    private sendStockSubscription(symbol: string): void {
        if (this.stockWs?.readyState === WebSocket.OPEN) {
            const sym = symbol.trim().toUpperCase();
            const subscribeMessage = {
                action: "subscribe",
                // T = tick trades (live price); AM = minute bars (OHLC candle)
                params: `T.${sym},AM.${sym}`,
            };
            this.stockWs.send(JSON.stringify(subscribeMessage));
            console.log(
                `📊 [WebSocket] Sent stock subscription: T.${sym}, AM.${sym}`,
            );
        }
    }

    private sendOptionSubscription(contract: string): void {
        if (this.optionWs?.readyState === WebSocket.OPEN) {
            const subscribeMessage = {
                action: "subscribe",
                params: `T.${contract}`,
            };
            this.optionWs.send(JSON.stringify(subscribeMessage));
            console.log(
                `📊 [WebSocket] Sent option subscription: T.${contract}`,
            );
        }
    }

    // Unsubscribe from stock aggregates
    unsubscribeFromStock(
        symbol: string,
        callback?: StockAggregateCallback,
    ): void {
        if (callback) {
            const callbacks = this.stockCallbacks.get(symbol);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }
                if (callbacks.length === 0) {
                    this.stockCallbacks.delete(symbol);
                    this.sendStockUnsubscription(symbol);
                }
            }
        } else {
            this.stockCallbacks.delete(symbol);
            this.sendStockUnsubscription(symbol);
        }

        console.log(
            `📊 [WebSocket] Unsubscribed from stock aggregates: ${symbol}`,
        );
    }

    // Unsubscribe from option trades
    unsubscribeFromOption(
        contract: string,
        callback?: OptionTradeCallback,
    ): void {
        if (callback) {
            const callbacks = this.optionCallbacks.get(contract);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }
                if (callbacks.length === 0) {
                    this.optionCallbacks.delete(contract);
                    this.sendOptionUnsubscription(contract);
                }
            }
        } else {
            this.optionCallbacks.delete(contract);
            this.sendOptionUnsubscription(contract);
        }

        console.log(
            `📊 [WebSocket] Unsubscribed from option trades: ${contract}`,
        );
    }

    private sendStockUnsubscription(symbol: string): void {
        if (this.stockWs?.readyState === WebSocket.OPEN) {
            const sym = symbol.trim().toUpperCase();
            const unsubscribeMessage = {
                action: "unsubscribe",
                params: `T.${sym},AM.${sym}`,
            };
            this.stockWs.send(JSON.stringify(unsubscribeMessage));
            console.log(
                `📊 [WebSocket] Sent stock unsubscription: T.${sym}, AM.${sym}`,
            );
        }
    }

    private sendOptionUnsubscription(contract: string): void {
        if (this.optionWs?.readyState === WebSocket.OPEN) {
            const unsubscribeMessage = {
                action: "unsubscribe",
                params: `T.${contract}`,
            };
            this.optionWs.send(JSON.stringify(unsubscribeMessage));
            console.log(
                `📊 [WebSocket] Sent option unsubscription: T.${contract}`,
            );
        }
    }

    private handleOptionMessage(data: any): void {
        if (data.ev === "T" && data.sym) {
            const callbacks = this.optionCallbacks.get(data.sym);
            if (callbacks) {
                callbacks.forEach((callback) => {
                    try {
                        callback(data as OptionTrade);
                    } catch (error) {
                        console.error(
                            "❌ [WebSocket] Error in option callback:",
                            error,
                        );
                    }
                });
            }
        }
    }

    private handleReconnect(type: "stock" | "option"): void {
        if (this.reconnectAttempts >= this.config.maxReconnectAttempts!) {
            console.error(
                `❌ [WebSocket] Max reconnection attempts reached for ${type}`,
            );
            return;
        }

        this.reconnectAttempts++;
        console.log(
            `🔄 [WebSocket] Reconnecting ${type} in ${this.config.reconnectInterval}ms (attempt ${this.reconnectAttempts})`,
        );

        setTimeout(() => {
            if (type === "stock") {
                this.connectStockAggregates();
            } else {
                this.connectOptionTrades();
            }
        }, this.config.reconnectInterval);
    }

    // Add connection status callback
    onConnectionChange(callback: ConnectionCallback): void {
        this.connectionCallbacks.push(callback);
    }

    private notifyConnectionCallbacks(connected: boolean): void {
        this.connectionCallbacks.forEach((callback) => {
            try {
                callback(connected);
            } catch (error) {
                console.error(
                    "❌ [WebSocket] Error in connection callback:",
                    error,
                );
            }
        });
    }

    // Get connection status
    isConnected(): { stock: boolean; option: boolean } {
        return {
            stock: this.stockWs?.readyState === WebSocket.OPEN,
            option: this.optionWs?.readyState === WebSocket.OPEN,
        };
    }

    // Disconnect all WebSockets
    disconnect(): void {
        if (this.stockWs) {
            this.stockWs.close();
            this.stockWs = null;
        }
        if (this.optionWs) {
            this.optionWs.close();
            this.optionWs = null;
        }
        this.stockCallbacks.clear();
        this.optionCallbacks.clear();
        this.connectionCallbacks = [];
        this.reconnectAttempts = 0;
        console.log("🔌 [WebSocket] All connections closed");
    }
}

// Working Polygon API key with Stocks Advanced+ tier (real-time WebSocket for stocks/ETFs)
const DEFAULT_POLYGON_KEY = "43m516m3o8bAv4VKInUqm5Cp4CbDmeSE";

// Main WebSocket service
const polygonWebSocketService = new PolygonWebSocketService({
    apiKey: process.env.REACT_APP_POLYGON_API_KEY || DEFAULT_POLYGON_KEY,
});

// Chart / stock real-time WebSocket: prefer POLYGON_STOCKS_API_KEY (stock real-time), then chart WS key, then main
const chartApiKey =
    (typeof process !== "undefined" &&
        process.env?.REACT_APP_POLYGON_STOCKS_API_KEY) ||
    (typeof process !== "undefined" &&
        process.env?.REACT_APP_POLYGON_CHART_WS_API_KEY) ||
    (typeof process !== "undefined" &&
        process.env?.REACT_APP_POLYGON_API_KEY) ||
    DEFAULT_POLYGON_KEY;
const chartPolygonWebSocketService = new PolygonWebSocketService({
    apiKey: chartApiKey,
    stockWsUrl: stockWsUrlFromEnv(REALTIME_STOCK_WS),
});

/** For live chart and hybrid gamma: uses FMP stock WebSocket when FMP_API_KEY set, else Polygon stock aggregates */
const chartStockWebSocketService = isFmpEnabled()
    ? new FmpStockWebSocketService(getFmpApiKey())
    : chartPolygonWebSocketService;

export { chartPolygonWebSocketService, chartStockWebSocketService };
export default polygonWebSocketService;
